'use client';

import { Credits } from './Credits';
import { FaGift } from '@/components/icons';
import { AchievementIcon } from '@/screens/AchievementsScreen/components/AchievementIcon';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import translations from './AchievementUnlockToast.translations';

interface Props {
  /** Catalog icon file name, e.g. `relic.png` (served from /public/achievements). */
  iconFile: string;
  /** Localised achievement title. */
  title: string;
  /** Full reward coins of the achievement. */
  rewardCredits?: number;
  /** Full reward XP of the achievement. */
  rewardXp?: number;
  /** Whether the bonus was credited. false → reward locked behind deposits; show a claim hint. */
  claimed?: boolean;
  /** When set, the whole toast is a button (tap → open the achievement / claim sheet). */
  onClick?: () => void;
}

/**
 * In-app notification shown when a user unlocks an achievement live.
 * Rendered inside a sonner `toast.custom(...)` from
 * `useAchievementUnlockedSubscription`. iOS-26 liquid-glass surface (same
 * recipe as the tab bar) + amber reward chips. Tappable → trophy sheet.
 */
export function AchievementUnlockToast({
  iconFile,
  title,
  rewardCredits,
  rewardXp,
  claimed = true,
  onClick,
}: Props) {
  const { t } = useLocalTranslation(translations);
  const hasReward = !!rewardCredits || !!rewardXp;

  const content = (
    <>
      {/* Icon tile: blurred backdrop + sharp art (same technique as the list cards). */}
      <div className="relative z-10 h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-white/5">
        <div className="pointer-events-none absolute inset-0 scale-[1.5] opacity-40 blur-[14px]">
          <AchievementIcon iconFile={iconFile} alt="" fill className="object-contain" />
        </div>
        <div className="animate-shot-ach-icon-pop relative h-full w-full">
          <AchievementIcon iconFile={iconFile} alt={title} fill className="object-contain" />
        </div>
      </div>

      <div className="relative z-10 min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-400">{t('unlocked')}</div>
        <div className="truncate text-sm font-bold text-label-primary">{title}</div>
        {hasReward && claimed && (
          <div className="mt-1 flex items-center gap-2">
            {!!rewardCredits && (
              <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-400">
                +<Credits amount={rewardCredits} iconSize={11} />
              </span>
            )}
            {!!rewardXp && (
              <span className="text-xs font-semibold text-label-secondary">+{rewardXp} {t('xp')}</span>
            )}
          </div>
        )}
        {hasReward && !claimed && (
          // Unlocked but reward locked behind deposits — invite to claim.
          <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-amber-400">
            <FaGift size={10} />
            {!!rewardCredits && (
              <span className="inline-flex items-center gap-0.5">
                +<Credits amount={rewardCredits} iconSize={10} />
              </span>
            )}
            <span className="text-amber-400/90">· {t('claimReward')} →</span>
          </div>
        )}
      </div>
    </>
  );

  // One-shot gloss streak that sweeps across the glass on mount.
  const shine = (
    <span className="animate-shot-ach-shine pointer-events-none absolute inset-y-0 left-0 z-20 w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
  );

  // mx-auto: on mobile sonner stretches the toast <li> full-width, so a fixed-width card
  // pins to the left edge — auto side-margins re-centre it under the top-center toaster.
  const base =
    'animate-shot-ach-toast-in liquid-glass relative mx-auto flex w-[min(360px,calc(100vw-32px))] items-center gap-3 overflow-hidden rounded-2xl p-3 text-left backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125';

  if (!onClick) {
    return (
      <div className={base}>
        {content}
        {shine}
      </div>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${base} cursor-pointer transition active:scale-[0.99] active:opacity-90`}>
      {content}
      {shine}
    </button>
  );
}
